import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PublicNavbar } from './PublicNavbar';
import { Footer } from './Footer';
import { UtilityTopbar } from './marketing/utility/Topbar';
import { ReviseAnnounceBar, ReviseFooter, ReviseNavbar } from './marketing/revise/Chrome';
import { MarketingDataProvider } from './marketing/MarketingData';
import { useMarketingTheme, useTenant } from '../context/TenantContext';
import { applyTenantBranding, clearTenantBranding } from '../lib/tenantBranding';

/** Wraps every public marketing route. Stamps the active theme onto the
 *  shell so nav/footer/topbar all share the CSS scope, and provides the
 *  shared courses fetch so each section reads from one source. */
interface MarketingLayoutProps {
  /**
   * Which surface the shell is painting. Derived from the route by default.
   *
   * The split is really "pages drawn in the theme's own material" vs "pages
   * built from the app's light surfaces", and `landing` is the former: themes
   * that carry a full-page canvas (revise) apply it there only, because this
   * layout also wraps `/courses`, `/course/:id`, `/bundle/:id`, `/privacy` and
   * `/terms`, whose cards and type were drawn for a light ground.
   *
   * `/free` is on the `landing` side for that reason — it is bespoke revise
   * JSX like the landing page, not a light app surface, so on `page` it would
   * render navy-on-navy with none of the paper flip. Every
   * `data-marketing-surface` rule in app.css is revise-scoped, so this is
   * inert for the other three themes.
   *
   * Overridable so the dev preview harness, which renders the landing page at
   * `/dev/marketing-preview`, can say so rather than having prod code
   * special-case a dev path.
   */
  surface?: 'landing' | 'page';
}

/** Routes painted in the theme's own material rather than the app's light one. */
const THEME_CANVAS_ROUTES: ReadonlySet<string> = new Set(['/', '/free', '/ieb']);

export const MarketingLayout: React.FC<MarketingLayoutProps> = ({ surface }) => {
  const theme = useMarketingTheme();
  const { tenant } = useTenant();
  const { pathname } = useLocation();
  const resolvedSurface = surface ?? (THEME_CANVAS_ROUTES.has(pathname) ? 'landing' : 'page');

  /* The announce bar is OFF (Sept 2026, Dylan). It carried the free NSC
     papers offer on every logged-out page, which was right while Kula Revise
     sold one board and wrong the day it sold two: with the IEB live, an IEB
     student reading an IEB course page met an NSC banner above it. It had
     already been suppressed on `/ieb` for that reason, but `/course/:id`,
     `/courses` and `/bundle/:id` serve both boards from one layout and
     cannot be split by path.

     What this costs, and does not: the six free papers still show as cards
     on the landing page (see BrowseWindow's isFreePracticeLab filter), so
     the giveaway stays visible. What loses its only in-site link is the
     `/free` PAGE — which is by design a link to be forwarded (the DBE
     distribution plan), so a direct visit still works exactly as before.
     Restore by flipping this to true; the bar and its copy are untouched. */
  const SHOW_ANNOUNCE = false;

  // Public marketing routes (incl. /course/:id) must reflect the tenant's brand
  // palette. AppLayout applies this for the in-app shell, but the marketing shell
  // is a separate route tree — without this, --color-primary-* falls back to the
  // Kula default (e.g. the CourseDetail hero rendered red for every tenant).
  // Depend on the colour primitives (not the `tenant` object) so a new context
  // object identity with unchanged branding doesn't churn the <style> tag and
  // flicker the hero for a frame.
  const brandPrimary = tenant?.branding?.primaryColor;
  const brandSecondary = tenant?.branding?.secondaryColor;
  const brandPrimaryDark = tenant?.branding?.primaryColorDark;
  const brandSecondaryDark = tenant?.branding?.secondaryColorDark;
  useEffect(() => {
    if (brandPrimary || brandSecondary) {
      applyTenantBranding(brandPrimary, brandSecondary, brandPrimaryDark, brandSecondaryDark);
    }
    return () => clearTenantBranding();
  }, [brandPrimary, brandSecondary, brandPrimaryDark, brandSecondaryDark]);

  return (
    <MarketingDataProvider>
      <div
        className="marketing-shell min-h-screen flex flex-col font-sans"
        data-marketing-theme={theme}
        data-marketing-surface={resolvedSurface}
      >
        {theme === 'utility' && <UtilityTopbar />}
        {/* Revise brings its own masthead and footer rather than variants of
            the shared ones: its nav is anchor-driven off the section list and
            its footer carries provenance statements, neither of which the
            other three themes have a place for. */}
        {theme === 'revise' ? (
          <>
            {SHOW_ANNOUNCE && <ReviseAnnounceBar />}
            <ReviseNavbar />
            <main className="flex-1">
              <Outlet />
            </main>
            <ReviseFooter />
          </>
        ) : (
          <>
            <PublicNavbar />
            <main className="flex-1">
              <Outlet />
            </main>
            <Footer />
          </>
        )}
      </div>
    </MarketingDataProvider>
  );
};
